import { fetchRatesForBedbankOffersById } from 'actions/BedbankOfferActions'
import { fetchCalendarsByOccupancy } from 'actions/CalendarActions'
import { fetchAvailableRatesForOffer, fetchOfferById } from 'actions/OfferActions'
import { shallowEqual, useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useEffect, useMemo } from 'react'
import { getBundledFlightItems } from 'checkout/selectors/view/flights'
import { getItemOfferId, isBedbankItem, isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import { groupBy } from 'lib/array/arrayUtils'
import { getPackageUniqueKey } from 'lib/offer/offerUtils'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import { isHotelPackage, isLEHotel } from 'lib/offer/offerTypes'
import { MultiCartContext } from 'machines/cart/MultiCartMachineProvider'
import { useSelector } from '@xstate/react'
import { selectItemsByActiveVisibleCart } from 'machines/cart/selectors'

/**
 * XState-compatible version of useFetchHotelItemData
 * Fetches hotel and bedbank rates for multi-cart items from XState context
 */
function useFetchMultiCartHotelItemData() {
  const dispatch = useAppDispatch()
  const multiCartActorRef = MultiCartContext.useActorRef()

  const activeCartItemsById = useSelector(multiCartActorRef, selectItemsByActiveVisibleCart)
  const activeCartItems = useMemo(() => Object.values(activeCartItemsById), [activeCartItemsById])
  const hasMultiCartItems = activeCartItems.length > 0

  // Get other required data from Redux
  const {
    regionCode,
    currencyCode,
    existingOrder,
    bundledFlightItems,
    offers,
    calendarErrors,
    bypassRegionSettingId,
    bypassRegionExclusionOfferIds,
  } = useAppSelector((state) => {
    return {
      regionCode: state.checkout.cart.regionCode,
      currencyCode: state.checkout.cart.currencyCode,
      existingOrder: state.checkout.cart.existingOrder,
      bundledFlightItems: getBundledFlightItems(state),
      offers: state.offer.offers,
      calendarErrors: state.calendar.calendarErrors,
      bypassRegionSettingId: state.marketing.bypassRegionSettingId,
      bypassRegionExclusionOfferIds: state.marketing.bypassRegionExclusionOfferIds,
    }
  }, shallowEqual)

  const flightOrigin = bundledFlightItems[0]?.originAirportCode

  // Derive accommodation items with stable memoization
  const accommodationItems = useMemo(() => {
    return activeCartItems
      .filter(multiCartItem => multiCartItem.state !== 'deleted') // Filter out deleted items
      .map(multiCartItem => multiCartItem.item)
      .filter(item => isInstantBookingLEHotelItem(item) || isBedbankItem(item))
  }, [activeCartItems])

  // Derive item states with stable memoization
  const itemStates = useMemo(() => {
    return activeCartItems.reduce<Record<string, { deleted: boolean, saveForLater: boolean }>>((acc, multiCartItem) => {
      acc[multiCartItem.item.itemId] = {
        deleted: multiCartItem.state === 'deleted',
        saveForLater: multiCartItem.state === 'save_for_later',
      }
      return acc
    }, {})
  }, [activeCartItems])

  // Fetch offer data
  useEffect(() => {
    if (!hasMultiCartItems) return // Skip if no multi-cart items

    // Fetching one by one as currently sold out offers are not returned via the multi-fetch endpoint.
    // bypassRegionFilter is not persisted by the cart API, so we fall back to the marketing state
    // (restored from cookie on page load) to keep the display consistent with what checkout allows.
    accommodationItems.forEach(item => {
      const offerId = getItemOfferId(item)
      if (!offerId) return
      const itemBypass = isInstantBookingLEHotelItem(item) ? !!item.bypassRegionFilter : false
      const marketingBypass = !!bypassRegionSettingId && !bypassRegionExclusionOfferIds.includes(offerId)
      const bypassRegionFilter = itemBypass || marketingBypass
      dispatch(fetchOfferById(offerId, { bypassRegionFilter }))
    })
    existingOrder?.items.forEach(item => {
      dispatch(fetchOfferById(item.offerId))
    })
  }, [dispatch, accommodationItems, existingOrder, hasMultiCartItems, bypassRegionSettingId, bypassRegionExclusionOfferIds])

  // Get calendar data and rates for LE Hotels
  useEffect(() => {
    if (!hasMultiCartItems) return // Skip if no multi-cart items

    const instantBookingHotelItems = accommodationItems.filter(isInstantBookingLEHotelItem)
    const leHotelItemsByBookingKey = groupBy(instantBookingHotelItems, item => `${item.offerId}-${item.checkIn}-${item.checkOut}`)
    const leHotelItemGroups = leHotelItemsByBookingKey.values().map(items => ({
      offerId: items[0].offerId,
      items,
    })).toArray()

    leHotelItemGroups.forEach((itemGroup) => {
      const offer = offers[itemGroup.offerId]
      if (isLEHotel(offer) && 'packages' in offer) {
        const { checkIn, checkOut } = itemGroup.items[0]
        const occupancies = itemGroup.items.map((item) => item.occupancy)
        const uniqueKeys = new Set<string>(itemGroup.items.map((item) => getPackageUniqueKey(item.packageId, item.duration, item.roomRateId)))

        const packages = offer.packages.filter(isHotelPackage).filter(pkg => uniqueKeys.has(pkg.uniqueKey))

        // Calendar and available-rate data is region-filtered too, so it must be fetched with the
        // same bypass as the offer — otherwise the rates come back unavailable and the item still
        // shows "Sold out for your dates" even though the offer itself loaded fine.
        const bypassRegionFilter = !!itemGroup.items[0].bypassRegionFilter ||
          (!!bypassRegionSettingId && !bypassRegionExclusionOfferIds.includes(offer.id))

        // Check if any packages have errors before dispatching
        // This prevents infinite retry loops when API calls fail
        const hasErrorForPackages = packages.some(pkg => calendarErrors[pkg.uniqueKey])

        if (!hasErrorForPackages) {
          dispatch(fetchCalendarsByOccupancy(offer.id, {
            occupants: occupancies,
            packages,
            regionCode,
            flightOrigin,
            bypassRegionFilter,
          }))
        }

        if (offer.type === OFFER_TYPE_ALWAYS_ON && checkIn && checkOut) {
          occupancies.forEach(occupancy => {
            dispatch(fetchAvailableRatesForOffer(
              offer, {
                occupants: [occupancy],
                checkIn,
                checkOut,
                currencyCode,
                flightOrigin,
                bypassRegionFilter,
              },
            ))
          })
        }
      }
    })

    // Fetch bedbank rates - THIS IS THE KEY MISSING PIECE
    const bedbankItems = accommodationItems.filter(isBedbankItem).filter(item => !itemStates[item.itemId]?.deleted)
    const bedbankItemsByBookingKey = groupBy(bedbankItems, item => {
      if (itemStates[item.itemId]?.saveForLater || item.isAccomBundle) {
        // Items saved for later should have their rates fetched individually
        return item.itemId
      } else {
        return `${item.offerId}-${item.checkIn}-${item.checkOut}-${item.roomRateId}`
      }
    })
    const bedbankItemGroups = bedbankItemsByBookingKey.values().map(items => ({
      offerId: items[0].offerId,
      items,
    })).toArray()

    bedbankItemGroups.forEach((itemGroup) => {
      const { checkIn, checkOut } = itemGroup.items[0]
      dispatch(fetchRatesForBedbankOffersById([itemGroup.offerId], itemGroup.items.map((item) => item.occupancy), checkIn, checkOut))
    })
  }, [accommodationItems, calendarErrors, currencyCode, dispatch, flightOrigin, offers, regionCode, itemStates, hasMultiCartItems, bypassRegionSettingId, bypassRegionExclusionOfferIds])
}

export default useFetchMultiCartHotelItemData
